import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {RegistrationService} from '@services/registration.service';
import {defaultStammdatenResource, StammdatenResource} from '@services/resource/registration-resource';
import {StammdatenComponent} from '@app/stammdaten/stammdaten.component';
import {ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {catchError, switchMap, tap} from 'rxjs/operators';
import {TrackingService} from '@services/tracking.service';
import {TrackingResource} from '@services/resource/tracking-resource';
import {RegistrationLeadTracking, SignupTrackLeadComponent} from '@app/signup/signup-track-lead/signup-track-lead.component';
import {Observable, Subscription, throwError} from 'rxjs';
import {MarkantCheckbox, MarkantElementsModule} from '@markant/elements-angular';
import {environment} from '@environments/environment';
import {RouteBuilder} from '@util/route-builder';
import {ToolbarComponent} from '@app/toolbar/toolbar.component';
import {HeaderComponent} from '@app/header/header.component';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {RouterLink} from '@angular/router';
import {SignupErrorComponent} from '@app/signup/signup-error-dialog/signup-error.component';
import {CommonModule} from '@angular/common';
import {FreshdeskSupportComponent} from '@widgets/freshdesk-support/freshdesk-support.component';

@Component({
  selector: 'app-signup',
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ToolbarComponent,
    HeaderComponent,
    MatProgressSpinner,
    MarkantElementsModule,
    RouterLink,
    StammdatenComponent,
    SignupTrackLeadComponent,
    SignupErrorComponent,
    ReactiveFormsModule,
    FreshdeskSupportComponent,
  ],
})
export class SignupComponent implements OnInit, OnDestroy {
  lead: RegistrationLeadTracking;
  dialogState = false;
  @ViewChild(StammdatenComponent, {static: true})
  stammdatenComponent: StammdatenComponent;

  @ViewChild(MarkantCheckbox, {static: true})
  tosAccepted: MarkantCheckbox;

  @ViewChild(SignupTrackLeadComponent, {static: true})
  signupTrackLeadComponent: SignupTrackLeadComponent;

  state: 'form' | 'process' | 'submitted' | 'maintenance' | 'error' = 'form';

  private readonly pollingSub: Subscription;
  errorMessage = '';

  public initialValue: StammdatenResource = defaultStammdatenResource;
  currentValue?: StammdatenResource | null;
  signupError: any;
  test = true;
  public signupForm: UntypedFormGroup = new UntypedFormGroup({
    tosAccepted: new UntypedFormControl(false, [Validators.requiredTrue]),
  });

  constructor(
    private readonly registrationService: RegistrationService,
    private readonly trackingService: TrackingService,
  ) {}

  ngOnInit() {
    this.stammdatenComponent.valueChanges.subscribe((n: StammdatenResource | null) => {
      this.currentValue = n;
    });
    this.signupTrackLeadComponent.currentValue().subscribe((n: RegistrationLeadTracking) => {
      this.lead = n;
    });
  }

  public onSubmit() {
    this.stammdatenComponent.markAsTouched();

    this.signupForm.markAsTouched();

    this.stammdatenComponent.signupForm.updateValueAndValidity();
    if (!this.currentValue) {
      this.stammdatenComponent.validateInputs();
      return false;
    }
    if (!this.stammdatenComponent.signupForm.valid || !this.signupForm.valid) {
      this.stammdatenComponent.validateInputs();
      if (!this.signupForm.get('tosAccepted')?.valid) {
        this.errorMessage += '- Datenschutz- und Nutzungsbedinungen';
      }
      return false;
    }

    this.state = 'process';

    const rr = this.currentValue;

    /* send registration data */

    this.sendRegistrationWithOptimism(rr);

    return true;
  }

  openDialogError() {
    this.signupError = true;
    this.dialogState = true;
  }

  /* Eigentlich ein temporärer Fix */
  sendRegistrationWithOptimism(rr: StammdatenResource): void {
    this.registrationService
      .register(rr)
      .pipe(
        tap(() => (this.state = 'submitted')),
        catchError((err) => {
          this.state = 'error';
          return throwError(err);
        }),
        switchMap((proc) => {
          if (proc === 'User exsists') {
            this.state = 'form';
            this.openDialogError();
            return new Observable<void>();
          } else if (proc.id) {
            this.lead.startupId = proc.id;
            return this.trackingService.track(new TrackingResource('VOILA_REGISTRATION', this.lead));
          } else {
            return new Observable<void>();
          }
        }),
      )
      .subscribe();
  }

  ngOnDestroy() {
    if (this.pollingSub) {
      this.pollingSub.unsubscribe();
    }
  }

  buildSignupBackLink(): string {
    if (environment.production) {
      return 'https://voila-startups.com';
    }
    return '';
  }

  get routes() {
    return {
      privacy: RouteBuilder.privacy().build(),
      termsOfUse: RouteBuilder.termsOfUse().build(),
    };
  }
}
